import React, { useEffect, useState } from "react";
import { FaVideo, FaBars, FaTimes, FaPhoneAlt } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { ref, push, onValue, off } from "firebase/database";
import { auth, database } from "../../firebaseconfig";
import { useCall } from "../CallContext"; // 🟦 IMPORT CONTEXT

const getConsistentRoomId = (fromId, toId) => {
  const sortedIds = [fromId, toId].sort();
  return `${sortedIds[0]}_${sortedIds[1]}`;
};

function Messages() {
  // 🟦 Using Data from Global Context
  const { 
    globalUser: user, 
    globalTeamMembers: teamMembers, 
    isGlobalLoading: isLoading,
    startOutgoingCall, // Global call function
    isInGlobalCall,    // Global call status (agar aap Context se pass kar rahe hain)
    leaveGlobalCall    // Global hangup function (agar aap Context se pass kar rahe hain)
  } = useCall();

  const [sidebarUsers, setSidebarUsers] = useState([]);
  const [selectedPeer, setSelectedPeer] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Set up Sidebar Users when Global Data is ready
  useEffect(() => {
    if (user && teamMembers.length > 0) {
      let filtered = [];
      const adminTypes = ["USERADMIN", "USER", "ADMIN", "COMPANY", "USERDISPATCHER"];
      
      if (adminTypes.includes(user.userType)) {
        filtered = teamMembers.filter((u) => u.userType === "DRIVER");
      } else if (user.userType === "DRIVER") {
        filtered = teamMembers.filter((u) => adminTypes.includes(u.userType));
      }
      setSidebarUsers(filtered);
      if (filtered.length > 0 && !selectedPeer) setSelectedPeer(filtered[0]);
    }
  }, [user, teamMembers]);

  // Resize Handlers
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setShowSidebar(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Messages Sync
  useEffect(() => {
    if (!user || !selectedPeer || !selectedPeer._id) return;
    const userId = user._id; // 🟦 Use Backend ID instead of Firebase UID
    const peerId = selectedPeer._id;
    const chatRoomId = getConsistentRoomId(userId, peerId);
    const messagesRef = ref(database, `messages/${chatRoomId}`);

    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.entries(data).map(([id, msg]) => ({
          id, from: msg.from, to: msg.to, msg: msg.text,
          time: new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          type: msg.from === userId ? "sent" : "received",
          timestamp: msg.timestamp,
        })).sort((a, b) => a.timestamp - b.timestamp);
        setMessages(messageList);
      } else setMessages([]);
    });
    return () => off(messagesRef);
  }, [user, selectedPeer]);

  // Auto scroll
  useEffect(() => {
    const chatContainer = document.querySelector(".overflow-y-auto-chat");
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, [messages]);

  const handleSendMessage = () => {
    if (!selectedPeer || !message.trim() || !user || !selectedPeer._id) return;
    const chatRoomId = getConsistentRoomId(user._id, selectedPeer._id);
    push(ref(database, `messages/${chatRoomId}`), { from: user._id, to: selectedPeer._id, text: message, timestamp: Date.now() })
      .then(() => setMessage("")).catch((error) => alert(error.message));
  };

  const handleSelectPeer = (peer) => { 
    setSelectedPeer(peer); 
    setMessages([]); 
  };

  return (
    <div className="text-white relative h-[90vh]">
      {isLoading ? (
        <div className="flex justify-center items-center h-full"><p>Loading chat data...</p></div>
      ) : (
        <div className="mx-auto grid md:grid-cols-4 gap-4 h-full relative">
          
          {isMobile && showSidebar && (
            <button onClick={() => setShowSidebar(false)} className="fixed top-4 right-24 z-50 p-2 bg-[#1f1a3a] rounded-full md:hidden">
              <FaTimes />
            </button>
          )}

          <div className={`bg-[#1f1a3a] rounded-xl overflow-y-auto p-4 md:col-span-1 ${showSidebar ? 'fixed inset-0 z-40 w-[85%] max-w-sm' : 'hidden md:block'} lg:static lg:block lg:w-full`}>
            <h2 className="text-xl font-semibold mb-4 text-white">Chats</h2>
            <input type="text" placeholder="Search by name" className="w-full mb-4 p-2 rounded-md text-black" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            {sidebarUsers.filter((u) => u.fullName.toLowerCase().includes(searchQuery.toLowerCase())).map((u) => (
              <div key={u._id} className={`flex items-center mb-2 gap-3 p-2 rounded-lg cursor-pointer ${selectedPeer && selectedPeer._id === u._id ? 'bg-blue-500 text-white' : 'bg-white text-black'}`} onClick={() => { handleSelectPeer(u); if (isMobile) setShowSidebar(false); }}>
                <img src={u.image || 'https://via.placeholder.com/40'} alt="avatar" className="rounded-full w-10 h-10" />
                <div className="flex-1 overflow-hidden">
                  <h4 className="font-semibold text-sm truncate">{u.fullName}</h4>
                  <p className="text-xs truncate">{u.email}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`bg-[#1e1b42] rounded-2xl flex flex-col justify-between h-full overflow-hidden ${showSidebar ? 'hidden md:block' : 'col-span-3 md:col-span-3'}`}>
            <div className="bg-gradient-to-r from-[#2f0f5d] to-[#4b00c9] p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                {isMobile && (<button onClick={() => setShowSidebar(true)} className="p-1 mr-2"><FaBars /></button>)}
                {selectedPeer ? (
                  <>
                    <img src={selectedPeer.image || "https://via.placeholder.com/40"} alt="avatar" className="w-10 h-10 md:w-12 md:h-12 rounded-full" />
                    <div>
                      <h3 className="font-semibold text-sm md:text-lg">{selectedPeer.fullName}</h3>
                      <p className="text-xs text-gray-200">{selectedPeer.userType}</p>
                    </div>
                  </>
                ) : (<span className="text-sm md:text-base">Select a user to chat</span>)}
              </div>
              <div className="flex gap-2">
                {/* 🟦 Calls trigger using context now */}
                <button onClick={() => startOutgoingCall(selectedPeer, false)} disabled={isInGlobalCall || !selectedPeer || !user} className="p-2 md:px-3 md:py-2 bg-green-600 text-white rounded-full disabled:opacity-50"><FaPhoneAlt className="text-sm md:text-base" /></button>
                <button onClick={() => startOutgoingCall(selectedPeer, true)} disabled={isInGlobalCall || !selectedPeer || !user} className="p-2 md:px-3 md:py-2 bg-blue-600 text-white rounded-full disabled:opacity-50" title="Ensure both users start a video call"><FaVideo className="text-sm md:text-base" /></button>
                {isInGlobalCall && (<button onClick={leaveGlobalCall} className="p-2 md:px-3 md:py-2 bg-red-600 text-white rounded-full"><FiPhone className="text-sm md:text-base" /></button>)}
              </div>
            </div>

            <div className="flex-1 px-2 md:px-4 py-2 overflow-y-auto overflow-y-auto-chat flex flex-col gap-2">
              {messages.length === 0 ? (
                <p className="text-gray-400 text-center">No messages yet</p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`flex flex-col ${msg.type === "sent" ? "items-end" : "items-start"}`}>
                    <div className={`px-3 py-1 md:px-4 md:py-2 rounded-2xl max-w-[70%] md:max-w-xs text-xs md:text-sm ${msg.type === "sent" ? "bg-[#7b42f6] text-white" : "bg-white text-black"}`}>{msg.msg}</div>
                    <span className="text-[10px] text-gray-400 mt-1">{msg.time}</span>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center px-2 py-1 m-2 md:px-3 md:py-2 md:m-4 rounded-full bg-gradient-to-r from-[#421e93] to-[#6600cc]">
              <div className="flex-1 bg-white rounded-full px-2 py-1 md:px-4 md:py-2 flex items-center">
                <input type="text" placeholder="Write your message" className="flex-1 text-xs md:text-sm text-gray-700 placeholder-gray-400 focus:outline-none bg-white" value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleSendMessage(); }} disabled={!selectedPeer || !user} />
              </div>
              <button className="ml-1 px-2 py-1 md:ml-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-full disabled:opacity-50 text-xs md:text-sm" onClick={handleSendMessage} disabled={!selectedPeer || !message.trim() || !user}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;